import { RootStore } from "@intrasync/stores";
import {
  inventoryManagementCustomMethodServices,
  syncUnitSystemFromRootStore,
  type InventoryHostRootStore,
} from "@intrasync/inventory-management";
import { applyInventoryTabVisibility } from "./views/inventory/inventoryVisibleTabs";
import { applyQualityControlTabVisibility } from "./views/qualityControl/qualityControlVisibleTabs";
import rootStoreConfig from "./rootStoreConfig";
import apiClient from "api-client/apiClient";

interface Window {
  rootStore?: RootStore;
  dateFNS?: unknown;
  __STORE_COUNT__?: number;
  __MEMORY_MONITOR__?: unknown;
}

export function createRootStore(): RootStore {
  const app = apiClient(rootStoreConfig.mode, rootStoreConfig.apiOptions);

  // Inventory-module services with CUSTOM Feathers methods beyond CRUD
  // (svc.commit(...), svc.dispatch(...), …). A socketio-client service proxy
  // only exposes standard CRUD unless registered with an explicit `methods`
  // list, so wire these before the RootStore mounts its service stores.
  const connection = app.get("connection");
  if (connection) {
    for (const [path, methods] of Object.entries(
      inventoryManagementCustomMethodServices
    )) {
      app.use(path, connection.service(path), { methods: [...methods] });
    }
  }

  // Hide the inventory hub tabs NTX doesn't use. Presentation only — every
  // store stays mounted. Must run before the hub header first renders.
  applyInventoryTabVisibility();

  // Hide the QC hub's own Yard tab. @intrasync/yard-management is the only
  // usable yard at NTX (2026-08-31); the QC module ships a parallel one backed
  // by its own tables that the yard module cannot see. Presentation only.
  applyQualityControlTabVisibility();

  const finalConfig = { ...rootStoreConfig, app };
  const rootStore = new RootStore(finalConfig);

  // NTX works in Imperial. The module's unit system defaults to metric until a
  // host wires it (`getInventoryUnitSystem` falls back to "metric"), which is
  // why every measurement read metric on the 2026-08-06 walkthrough. Setting
  // the root-store preference and syncing flips every MeasureCell/PressureCell
  // grid AND the MeasureField/QuantityField dialog inputs, which then accept
  // and round-trip Imperial input.
  //
  // `syncUnitSystemFromRootStore` re-reads on login, and `getInventoryUnitSystem`
  // checks the signed-in user's own `unitSystem` first — so a per-user override
  // still wins if one is ever set.
  // `InventoryHostRootStore` is the module's own host-contract type; the real
  // RootStore satisfies it at runtime but not structurally (its `app.service`
  // returns a wider Feathers type), hence the cast.
  const inventoryHost = rootStore as unknown as InventoryHostRootStore;
  inventoryHost.inventoryUnitSystem = "imperial";
  syncUnitSystemFromRootStore(inventoryHost);

  // if (import.meta.env.MODE === "development") {
  (window as Window).rootStore = rootStore;
  // }

  return rootStore;
}
